/**
 * V17.5: NotificationBell Component
 *
 * Displays notification bell icon in header with:
 * - Unread count badge
 * - Dropdown panel with notification list
 * - Mark as read / Mark all as read actions
 * - Click to navigate to referenced entity
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, X, AlertTriangle, Info, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Notification,
  NotificationType,
  NotificationCategory,
  formatNotificationTime,
  getNotificationTypeBg,
} from '../services/notificationService';

interface NotificationBellProps {
  className?: string;
}

export function NotificationBell({ className = '' }: NotificationBellProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { language } = useLanguage();

  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    dismiss,
    newNotification,
    clearNewNotification,
  } = useNotifications();

  // V17.28: UI translations
  const translations = {
    lo: {
      notifications: 'ແຈ້ງເຕືອນ',
      markAllRead: 'ໝາຍວ່າອ່ານໝົດ',
      loading: 'ກຳລັງໂຫລດແຈ້ງເຕືອນ...',
      noNotifications: 'ບໍ່ມີແຈ້ງເຕືອນ',
      showing: 'ສະແດງ',
      notification: 'ແຈ້ງເຕືອນ',
      viewAll: 'ເບິ່ງທັງໝົດ →',
      viewDetails: 'ເບິ່ງລາຍລະອຽດ →',
      markAsRead: 'ໝາຍວ່າອ່ານແລ້ວ',
      dismiss: 'ຍົກເລີກ',
      unreadNotifications: 'ແຈ້ງເຕືອນທີ່ຍັງບໍ່ໄດ້ອ່ານ',
      // Category labels
      assignment: 'ມອບໝາຍ',
      rating: 'ປະເມີນ',
      slaAlert: 'ແຈ້ງເຕືອນ SLA',
      update: 'ອັບເດດ',
      system: 'ລະບົບ',
      rework: 'ສົ່ງກັບແກ້ໄຂ',
      rejection: 'ປະຕິເສດ',
      siteCheck: 'ກວດສະຖານທີ່',
    },
    en: {
      notifications: 'Notifications',
      markAllRead: 'Mark all read',
      loading: 'Loading notifications...',
      noNotifications: 'No notifications yet',
      showing: 'Showing',
      notification: 'notification',
      viewAll: 'View All →',
      viewDetails: 'View Details →',
      markAsRead: 'Mark as read',
      dismiss: 'Dismiss',
      unreadNotifications: 'unread notifications',
      // Category labels
      assignment: 'Assignment',
      rating: 'Rating',
      slaAlert: 'SLA Alert',
      update: 'Update',
      system: 'System',
      rework: 'Rework',
      rejection: 'Rejected',
      siteCheck: 'Site Check',
    },
  };
  const t = translations[language];

  // V17.28: Get localized title/message based on language preference
  const getLocalizedTitle = (notification: Notification): string => {
    if (language === 'lo' && notification.title_lao) {
      return notification.title_lao;
    }
    return notification.title;
  };

  const getLocalizedMessage = (notification: Notification): string => {
    if (language === 'lo' && notification.message_lao) {
      return notification.message_lao;
    }
    return notification.message;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show toast for new notifications
  useEffect(() => {
    if (newNotification) {
      // Auto-clear toast after 5 seconds
      const timeout = setTimeout(clearNewNotification, 5000);
      return () => clearTimeout(timeout);
    }
  }, [newNotification, clearNewNotification]);

  // Handle notification click - V17.8: Navigate to inbox with notification selected
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate to notification inbox with notification ID
    setIsOpen(false);
    navigate(`/notifications?id=${notification.id}`);
  };

  // Get icon for notification type
  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case NotificationType.SUCCESS:
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case NotificationType.WARNING:
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case NotificationType.ERROR:
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Info className="h-4 w-4 text-blue-500" />;
    }
  };

  // Get category label - V17.28: Now bilingual
  const getCategoryLabel = (category: NotificationCategory) => {
    switch (category) {
      case NotificationCategory.LEAD_ASSIGNMENT:
        return t.assignment;
      case NotificationCategory.LEAD_RATING:
        return t.rating;
      case NotificationCategory.LEAD_UPDATE:
        return t.update;
      case NotificationCategory.SLA_BREACH:
        return t.slaAlert;
      case NotificationCategory.TPO_REWORK:
        return t.rework;
      case NotificationCategory.TPO_REJECTION:
        return t.rejection;
      case NotificationCategory.SITE_CHECK_COMPLETED:
        return t.siteCheck;
      default:
        return t.system;
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative min-w-[44px] min-h-[44px] flex items-center justify-center p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        title={`${unreadCount} ${t.unreadNotifications}`}
      >
        <Bell className="h-5 w-5" />

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full min-w-[18px]">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel - V17.8: Mobile-responsive width, V17.16.73: Fixed mobile alignment */}
      {isOpen && (
        <div className="fixed left-2 right-2 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96 sm:max-w-[400px] bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <h3 className="font-semibold text-gray-900">{t.notifications}</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <CheckCheck className="h-4 w-4" />
                {t.markAllRead}
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-8 text-center text-gray-500">
                {t.loading}
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p>{t.noNotifications}</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-blue-50/50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    {/* Type Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {getTypeIcon(notification.type as NotificationType)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${getNotificationTypeBg(notification.type as NotificationType)}`}>
                          {getCategoryLabel(notification.category as NotificationCategory)}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatNotificationTime(notification.created_at, language)}
                        </span>
                      </div>

                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getLocalizedTitle(notification)}
                      </p>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {getLocalizedMessage(notification)}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0 flex items-center gap-1">
                      {!notification.is_read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title={t.markAsRead}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          dismiss(notification.id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title={t.dismiss}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer - V17.8: View All link to notification inbox */}
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {notifications.length > 0
                ? `${t.showing} ${notifications.length} ${t.notification}${language === 'en' && notifications.length !== 1 ? 's' : ''}`
                : t.noNotifications}
            </span>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              {t.viewAll}
            </button>
          </div>
        </div>
      )}

      {/* Toast for New Notification - V17.16.73: Fixed mobile alignment */}
      {newNotification && (
        <div className="fixed bottom-4 left-2 right-2 sm:left-auto sm:right-4 sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-[100] overflow-hidden animate-slide-up">
          <div className={`px-4 py-3 ${getNotificationTypeBg(newNotification.type as NotificationType)}`}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {getTypeIcon(newNotification.type as NotificationType)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {getLocalizedTitle(newNotification)}
                </p>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {getLocalizedMessage(newNotification)}
                </p>
              </div>
              <button
                onClick={clearNewNotification}
                className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => {
                navigate(`/notifications?id=${newNotification.id}`);
                clearNewNotification();
              }}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {t.viewDetails}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
